import * as React from 'react';

// --- Lib ---
import { parseShortcutKeys } from '../../../../app/rte/lib/tiptap-utils';

// --- Hooks ---
import { useTiptapEditor } from '../../../../app/rte/hooks/use-tiptap-editor';

// --- Tiptap UI ---
import type { UseSlashCommandTriggerConfig } from './use-slash-command-trigger';
import {
  SLASH_COMMAND_TRIGGER_SHORTCUT_KEY,
  useSlashCommandTrigger,
} from './use-slash-command-trigger';

// --- UI Components ---
import { Button } from '@cotera/client/v0/components/ui/button';
import { Badge } from '@cotera/client/v0/components/ui/badge';

import { Icon, Tooltip } from '@cotera/client/app/components';

export interface SlashCommandTriggerButtonProps
  extends Omit<React.ComponentProps<typeof Button>, 'type'>,
    UseSlashCommandTriggerConfig {
  /**
   * Optional text to display alongside the icon.
   */
  text?: string;
  /**
   * Optional show shortcut keys in the button.
   * @default false
   */
  showShortcut?: boolean;
}

export function SlashCommandShortcutBadge({
  shortcutKeys = SLASH_COMMAND_TRIGGER_SHORTCUT_KEY,
}: {
  shortcutKeys?: string;
}) {
  return <Badge variant="outline">{parseShortcutKeys({ shortcutKeys })}</Badge>;
}

/**
 * Button component for inserting slash commands in a Tiptap editor.
 *
 * For custom button implementations, use the `useSlashCommand` hook instead.
 */
export const SlashCommandTriggerButton = React.forwardRef<
  HTMLButtonElement,
  SlashCommandTriggerButtonProps
>(
  (
    {
      editor: providedEditor,
      node,
      nodePos,
      text,
      trigger = '/',
      hideWhenUnavailable = false,
      onTriggered,
      showShortcut = false,
      onClick,
      children,
      ...buttonProps
    },
    ref
  ) => {
    const { editor } = useTiptapEditor(providedEditor);
    const {
      isVisible,
      canInsert,
      handleSlashCommand,
      label,
      shortcutKeys,
      Icon: iconName,
    } = useSlashCommandTrigger({
      editor,
      node,
      nodePos,
      trigger,
      hideWhenUnavailable,
      onTriggered,
    });

    const handleClick = React.useCallback(
      (event: React.MouseEvent<HTMLButtonElement>) => {
        onClick?.(event);
        if (event.defaultPrevented) {
          return;
        }
        handleSlashCommand();
      },
      [handleSlashCommand, onClick]
    );

    if (!isVisible) {
      return null;
    }

    return (
      <Button
        type="button"
        variant="ghost"
        size="icon-sm"
        role="button"
        tabIndex={-1}
        disabled={!canInsert}
        data-disabled={!canInsert}
        aria-label={label}
        title={label}
        onClick={handleClick}
        {...buttonProps}
        ref={ref}
      >
        {children ?? (
          <Tooltip tooltipContent={label} side="auto">
            <Icon icon={iconName} className="tiptap-button-icon" />
            {text && <span className="tiptap-button-text">{text}</span>}
            {showShortcut && (
              <SlashCommandShortcutBadge shortcutKeys={shortcutKeys} />
            )}
          </Tooltip>
        )}
      </Button>
    );
  }
);

SlashCommandTriggerButton.displayName = 'SlashCommandTriggerButton';
